import React, {useState, useEffect} from 'react';
import {Card} from 'primereact/card';
import {TabView, TabPanel} from 'primereact/tabview';
import {useLocation, useNavigate} from 'react-router-dom';
import {UserBasicInfo} from '../service/userService';
import {OrganizationViewModel} from '../viewmodel/OrganizationViewModel';
import {ROUTES} from '@/core/constants/app';

import './OrganizationSettings.scss';
import {MemberView} from './MemberView';
import {OrganizationInfoTab} from './OrganizationInfoTab';
import {TeamTab} from './TeamTab';

interface OrganizationSettingsProps {
  basicInfo?: UserBasicInfo | null;
  loading?: boolean;
}

const TAB_ROUTES = [ROUTES.SETTINGS_ORGANIZATION_INFO, ROUTES.SETTINGS_ORGANIZATION_TEAM];

export const OrganizationSettings = ({basicInfo}: OrganizationSettingsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const {
    userPartner,
    organizationData,
    loading,
    updateOrganization,
    fetchUserPartner,
    deleteOrganization,
    deleteLoading,
    sectorNames,
  } = OrganizationViewModel();

  const getActiveIndexFromPath = (): number => {
    const currentPath = location.pathname;
    const index = TAB_ROUTES.findIndex((route) => route === currentPath);
    return index >= 0 ? index : 0;
  };

  const activeIndex = getActiveIndexFromPath();

  useEffect(() => {
    if (location.pathname === ROUTES.SETTINGS_ORGANIZATION) {
      navigate(ROUTES.SETTINGS_ORGANIZATION_INFO, {replace: true});
    }
  }, [location.pathname, navigate]);

  const handleTabChange = (index: number) => {
    const route = TAB_ROUTES[index];
    if (route) {
      navigate(route);
    }
  };

  const isAdmin =
    basicInfo?.partner && typeof basicInfo?.partner === 'object' && basicInfo.partner.status === 'admin';
  const isMember =
    basicInfo?.partner && typeof basicInfo?.partner === 'object' && basicInfo.partner.status === 'member';

  const handleDeleteOrganization = async () => {
    await deleteOrganization();
    setShowDeleteDialog(false);
  };

  const renderTabView = (isAdminView: boolean) => (
    <TabView activeIndex={activeIndex} onTabChange={(e) => handleTabChange(e.index)}>
      <TabPanel header="Info">
        {isAdminView ? (
          <OrganizationInfoTab
            organizationData={organizationData}
            onSave={updateOrganization}
            saveLoading={loading}
            onDelete={handleDeleteOrganization}
            deleteLoading={deleteLoading}
            sectorNames={sectorNames}
            showDeleteDialog={showDeleteDialog}
            onShowDeleteDialog={() => setShowDeleteDialog(true)}
            onCancelDelete={() => setShowDeleteDialog(false)}
          />
        ) : (
          <MemberView partnerData={userPartner?.partner_data || null} />
        )}
      </TabPanel>
      <TabPanel header="Team">
        <TeamTab
          partnerMembers={userPartner?.partner_members || []}
          refetchOrganization={fetchUserPartner}
          isAdmin={isAdminView}
        />
      </TabPanel>
    </TabView>
  );

  if (isMember) {
    return (
      <div className="organization-settings">
        <Card>{renderTabView(false)}</Card>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="organization-settings">
        <Card>{renderTabView(true)}</Card>
      </div>
    );
  }

  return (
    <div className="organization-settings">
      <Card>
        <div className="text-center py-4">
          {loading ? 'Loading organization data...' : 'No organization data available'}
        </div>
      </Card>
    </div>
  );
};